// The Overview card for linking a real job to its bid, and the picker it opens.
//
// Why: see BidLink.tsx. Kept in its own file because it uses workspace-route classes
// (`pw-card`, `ov-*`) and BidLink.tsx is also imported by the projects list.

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { projectsApi } from '@/lib/apiClient';
import { invalidateBidLinkQueries } from '@/lib/queryClient';
import { isRealJobNumber } from '@/lib/projectStage';
import { Modal, Button } from '@/components';
import type { OverviewProject } from './OverviewEdit';
import { PlaceholderRows, useDebounced } from './BidLink';

// ─── Overview: link an existing job ─────────────────────────────────────────

function LinkToBidModal({ project, onClose }: { project: OverviewProject; onClose: () => void }) {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState<number | null>(null);
  const q = useDebounced(search.trim(), 250);
  const list = useQuery({
    queryKey: ['bid-placeholders', q],
    queryFn: () => projectsApi.bidPlaceholders(q),
    staleTime: 15_000,
  });
  const items = list.data ?? [];
  // A pick that has dropped out of the list (the PM kept typing) is not on screen, so it
  // must not be linkable: the button would be live with nothing visibly selected.
  const pickedId = picked != null && items.some((m) => m.id === picked) ? picked : null;

  const link = useMutation({
    // The placeholder travels as the mutation's variable, so what is invalidated afterwards
    // is the one that was linked, whatever the list shows by then.
    mutationFn: (placeholderId: number) => projectsApi.linkBid(project.id, placeholderId),
    onSuccess: (_done, placeholderId) => {
      // The job's own detail and the lists, the placeholder it replaced (its own detail, and
      // the open list it has just left), and the schedule and worklists the archive moves.
      invalidateBidLinkQueries(qc, project.id, placeholderId);
      onClose();
    },
    // A refusal usually means the list is out of date: another PM linked that placeholder,
    // or its bid left chance. Re-read it, or the modal keeps offering a bid that cannot be
    // linked, for as long as the 15 seconds the list is held fresh.
    onError: () => {
      qc.invalidateQueries({ queryKey: ['bid-placeholders'] });
    },
  });

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`Link job ${project.jobNumber} to a bid`}
      maxWidth={560}
      closeOnBackdropClick={false}
      footer={<>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={() => pickedId != null && link.mutate(pickedId)} disabled={pickedId == null || link.isPending} isLoading={link.isPending}>
          Link to this bid
        </Button>
      </>}
    >
      <div style={{ fontSize: 12, color: 'var(--gray)', marginBottom: 10 }}>
        Pick the Bid Tracker placeholder that is this same job. This job takes over the bid and the placeholder is archived
        (not deleted). This job keeps its own title, team and dates.
      </div>
      <input
        aria-label="Search bids"
        placeholder="Search by name, client or SYSTAC number"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        autoFocus
        style={{ border: '1px solid var(--border)', borderRadius: 'var(--ew-r-md)', padding: '6px 10px', fontSize: 13, width: '100%', marginBottom: 10 }}
      />
      {list.isError ? (
        <div style={{ fontSize: 12, color: '#8F2414' }}>Couldn’t load the bids. Try again.</div>
      ) : list.isLoading ? (
        <div style={{ fontSize: 12, color: 'var(--gray)' }}>Loading…</div>
      ) : items.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--gray)' }}>No open bid placeholders match.</div>
      ) : (
        <PlaceholderRows items={items} name="bid-link" value={pickedId} onChange={setPicked} />
      )}
      {link.isError && (
        <div role="alert" style={{ marginTop: 10, padding: '8px 12px', background: '#FBE4DF', color: '#8F2414', borderRadius: 'var(--ew-r-md)', fontSize: 12 }}>
          {(link.error as Error)?.message ?? 'The link failed.'}
        </div>
      )}
    </Modal>
  );
}

/**
 * Overview card for a REAL job (a Sage number): says whether it is tied to a bid, and
 * offers to tie it. Editors only. Hidden on a placeholder (it IS the bid's) and on
 * import and maintenance stubs, which have no bid to find.
 */
export function BidLinkSection({ project, canEdit }: { project: OverviewProject; canEdit: boolean }) {
  const [open, setOpen] = useState(false);
  if (!canEdit || !isRealJobNumber(project.jobNumber)) return null;

  const linked = project.sourceBidId != null;
  return (
    <div className="pw-card">
      <div className="ov-sec-hd"><h2 className="ov-sec-title">Bid Tracker</h2></div>
      <div style={{ padding: '10px var(--ew-s-4)', display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
        {linked ? (
          <span style={{ fontSize: 13 }}>Linked to bid #{project.sourceBidId}.</span>
        ) : project.isArchived ? (
          // The server refuses an archived job (it would take an active bid and stay hidden).
          <span style={{ fontSize: 12, color: 'var(--gray)' }}>
            This job is archived. Restore it to link it to a bid.
          </span>
        ) : (
          <>
            <span style={{ fontSize: 12, color: 'var(--gray)', flex: '1 1 260px' }}>
              Not linked to a bid. If this job was created here by hand for a job that is already a bid in Bid Tracker,
              link it so the bid gets this job number and its later updates reach this job.
            </span>
            <button className="ew-btn-outline" onClick={() => setOpen(true)}>Link to a bid…</button>
          </>
        )}
      </div>
      {open && <LinkToBidModal project={project} onClose={() => setOpen(false)} />}
    </div>
  );
}
